import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { format } from "date-fns";
import { 
    useTheme, 
    useMediaQuery,
    Box, 
    Typography, 
    CircularProgress, 
    Button,
} from '@mui/material';
import { DataGrid } from "@mui/x-data-grid";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'state/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { capitalizeFirstLetter } from 'state/helpers';
import CustomDataGridToolbar from 'components/CustomDataGridToolbar';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import InvoiceDialog from 'scenes/inflightSales/InvoiceDialog';


const Invoices = () => {
    useAuthCheck();
    const user = useSelector((state) => state.auth.user);
    const crewCode = user.crewCode;
    const timeMode = useSelector((state) => state.auth.timeMode);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { startDate, endDate } = useDateRangeContext();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);    

    const [rowSelectionModel, setRowSelectionModel] = useState({
        type: 'include',
        ids: new Set(),
    });    

    const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
    const [selectedCrewCode, setSelectedCrewCode] = useState('');
    const [selectedCrewName, setSelectedCrewName] = useState('');
    const [selectedFlights, setSelectedFlights] = useState([]);
    const [crewMembers, setCrewMembers] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);

    const [invoices, setInvoices] = useState([]);
    const crewRanks = useSelector((state) => state.auth.crewRanks);

    const handleInvoices = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.post('/inflightsales/invoicesbycrewcode', { crewCode, startDate, endDate });
            setInvoices(res.data);
        } catch (error) {
            setError(error);
            toast.error("Failed to fetch invoices: " + error);
        } finally {
            setLoading(false);
        }
    }, [crewCode, startDate, endDate]);

    useEffect(() => {
        handleInvoices();      
    }, [handleInvoices]);

    const handleViewClick = useCallback(async () => {     
        setOpenInvoiceDialog(true);
    }, []);

    const handleInvoiceSaved = () => {
        handleInvoices();
    };
    
    const handleCloseInvoiceDialog = () => {
        setSelectedInvoiceId(null);
        setSelectedCrewCode(null);
        setSelectedCrewName(null);
        setSelectedFlights([]);  
        setCrewMembers([]);
        setSelectedProducts([]);
        setOpenInvoiceDialog(false); 
    };

    const columns = useMemo(() => {
        const categoryInfo = invoices.reduce(
            (acc, invoice) => {
                const categorySummary = invoice?.categorySummary || {};
                Object.entries(categorySummary).forEach(([category, categoryData]) => {
                    acc.categories.add(category);
                    Object.keys(categoryData).forEach(field => acc.fields.add(field));
                });
                return acc;
            },
            { categories: new Set(), fields: new Set() }
        );
        
        // Convert sets to arrays
        const categories = Array.from(categoryInfo.categories)?.filter(category => category !== 'Equipment');
        const allFields = Array.from(categoryInfo.fields);
        
        const categoryColumns = categories.flatMap(category =>
            allFields.map(field => ({
                field: `${category.toLowerCase().replace(/&/g, "n")}${field}`,
                headerName: `${category}${capitalizeFirstLetter(field)}`,
                headerClassName: "theme-column--header",
                width: 150,
                align: "right",
                renderCell: (params) => {
                    const value = params?.row?.categorySummary?.[category]?.[field];
                    return <Box>{value ? value.toLocaleString() : ""}</Box>;
                },
            }))
        );


        return [
            {
                field: "no",
                headerName: "No",
                headerClassName: 'theme-column--header',
                width: 30,
                filterable: false,      
                sortable: false,
                renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
            },
            {
                field: "crewCode",
                headerName: "Code",
                headerClassName: 'theme-column--header',
                width: 40,
            },
            {
                field: "crewName",
                headerName: "InputBy",
                headerClassName: 'theme-column--header',
                width: 280,
            },
            {
                field: "createdAt",
                headerName: "CreatedAt",
                headerClassName: 'theme-column--header', 
                width: 130, 
                pinned: 'left',
                valueFormatter: (value) => {
                    if (value == null) {
                        return '';
                    }
                    return `${format(value, "HH:mm:ss dd/MM/yy")}`;
                },
            },
            {
                field: "updatedAt",
                headerName: "UpdatedAt",
                headerClassName: 'theme-column--header', 
                width: 130, 
                pinned: 'left',
                valueFormatter: (value) => {
                    if (value == null) {
                        return '';
                    }
                    return `${format(value, "HH:mm:ss dd/MM/yy")}`;
                },
            },
            {
                field: timeMode === 'UTC' ? "flightDate" : "flightDateLocalBase",
                headerName: "RouteDate",
                headerClassName: 'theme-column--header',
                width: 100, 
                pinned: 'left',
                valueFormatter: (value) => {
                    if (value == null) {
                        return '';
                    }
                    return `${format(value, "dd/MM/yy")}`;
                },
            },
            
            {
                field: "flightNo",
                headerName: "FlightNo",
                width: 200,
                pinned: 'left',
                headerClassName: 'theme-column--header',
                cellClassName: "name-column--cell",
            },        
            {
                field: "crewRoute",
                headerName: "CrewRoute",
                headerClassName: 'theme-column--header',
                width: 200,
            },        
            {
                field: 'selectedFlights',
                headerName: 'FlightList',
                width: isMobile ? 265 : 280,
                renderCell: (params) => {
                    const sortedFlights = [...(params.row.selectedFlights || [])].sort((a, b) => {
                        const dateComparison = new Date(a.flightDate) - new Date(b.flightDate);
                        if (dateComparison !== 0) return dateComparison; 
                        return a.etd.localeCompare(b.etd); 
                    });                
                        
                    return (
                        <Box
                            sx={{
                                height: '100%',
                                alignItems: 'flex-start',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                            }}
                        >
                            {sortedFlights.map((flight, index) => (
                                <Box
                                    key={index}
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'right',
                                            whiteSpace: 'nowrap',
                                            width: 32,
                                        }}
                                    >
                                        {flight.flightNo}
                                    </Typography>
                                    
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'center',
                                            width: 45,
                                        }}
                                    >                                    
                                        {timeMode === 'UTC' ? format(flight.flightDate, "ddMMMyy") : format(flight.flightDateLocalBase, "ddMMMyy")}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'center',
                                            width: 30,
                                        }}
                                    >
                                        {timeMode === 'UTC' ? flight.etd : flight.etdLocalBase}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'center',                                        
                                            width: 25,
                                        }}
                                    >
                                        {flight.dep}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'center',                                        
                                            width: 25,
                                        }}
                                    >
                                        {flight.arr}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'center',
                                            width: 30,
                                        }}
                                    >
                                        {timeMode === 'UTC' ? flight.eta : flight.etaLocalBase}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    );
                },
            },
            {
                field: 'crewMembers',
                headerName: 'CrewMembers',
                width: isMobile ? 330 : 350,
                renderCell: (params) => {
                    const sortedCrewMembers = params.row.crewMembers
                        ?.map(member => {
                            const rank = crewRanks.find(rank => rank.rankCode === member.post) || {};
                            return { ...member, rankPriority: rank.rankPriority || Infinity };
                        })
                        .sort((a, b) => a.rankPriority - b.rankPriority);
                        
                    return (
                        <Box
                            sx={{
                                height: '100%',
                                alignItems: 'flex-start',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                            }}
                        >
                            {sortedCrewMembers?.map((member, index) => (
                                <Box
                                    key={index}
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'right',
                                            whiteSpace: 'nowrap',
                                            width: 30,
                                        }}
                                    >
                                        {member.crewCode}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'left',
                                        }}
                                    >
                                        {member.crewName}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'center',
                                        }}
                                    >
                                        ({member.post} | {member.base})
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    );
                },
            },
            {
                field: 'preorderProducts',
                headerName: 'PreorderProducts',
                width: isMobile ? 320 : 380,
                renderCell: (params) => {
                    const filteredProducts = params.row.products
                        ?.filter(product => product.preorderQuantity > 0);
                        
                    return (
                        <Box
                            sx={{
                                height: '100%',
                                alignItems: 'flex-start',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                            }}
                        >
                            {filteredProducts?.map((product, index) => {
                                const totalQuantity = product.preorderQuantity || 0;                            
                                return (
                                    <Box
                                        key={index}
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                textAlign: 'right',
                                                whiteSpace: 'nowrap',
                                                width: 12,
                                            }}
                                        >
                                            {`${index + 1}. `}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                textAlign: 'left',
                                                whiteSpace: 'nowrap',
                                                width: isMobile ? 220 : 280,
                                            }}
                                        >
                                            {product.productName}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                textAlign: 'right',
                                                whiteSpace: 'nowrap',
                                                width: 20,
                                            }}
                                        >
                                            {totalQuantity}
                                        </Typography>
                                    </Box>
                                )}
                            )}
                        </Box>
                    );
                },
            }, 
            {
                field: 'directProducts',
                headerName: 'DirectProducts',
                width: isMobile ? 320 : 380,
                renderCell: (params) => {
                    const filteredProducts = params.row.products
                        ?.filter(product => product.directQuantity > 0);
                        
                    return (
                        <Box
                            sx={{
                                height: '100%',
                                alignItems: 'flex-start',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                            }}
                        >
                            {filteredProducts?.map((product, index) => {
                                const totalQuantity = product.directQuantity || 0;                            
                                return (
                                    <Box
                                        key={index}
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                textAlign: 'right',
                                                whiteSpace: 'nowrap',
                                                width: 12,
                                            }}
                                        >
                                            {`${index + 1}. `}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                textAlign: 'left',
                                                whiteSpace: 'nowrap',
                                                width: isMobile ? 220 : 280,
                                            }}
                                        >
                                            {product.productName}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                textAlign: 'right',
                                                whiteSpace: 'nowrap',
                                                width: 20,
                                            }}
                                        >
                                            {totalQuantity}
                                        </Typography>
                                    </Box>
                                )}
                            )}
                        </Box>
                    );
                },
            },
            // {
            //     field: 'products',
            //     headerName: 'Products',
            //     width: isMobile ? 320 : 380,
            //     renderCell: (params) => {
            //         const filteredProducts = params.row.products
            //             ?.filter(product => product.preorderQuantity > 0 || product.directQuantity > 0);
                        
            //         return (
            //             <Box
            //                 sx={{
            //                     height: '100%',
            //                     alignItems: 'flex-start',
            //                     overflowY: 'auto',
            //                     overflowX: 'hidden',
            //                 }}
            //             >
            //                 {filteredProducts?.map((product, index) => {
            //                     const totalQuantity = (product.preorderQuantity || 0) + (product.directQuantity || 0);                            
            //                     return (
            //                         <Box
            //                             key={index}
            //                             display="flex"
            //                             alignItems="center"
            //                             gap={1}
            //                         >
            //                             <Typography
            //                                 variant="body2"
            //                                 sx={{
            //                                     textAlign: 'right',
            //                                     whiteSpace: 'nowrap',
            //                                     width: 12,
            //                                 }}
            //                             >
            //                                 {`${index + 1}. `}
            //                             </Typography>
            //                             <Typography
            //                                 variant="body2"
            //                                 sx={{
            //                                     textAlign: 'left',
            //                                     whiteSpace: 'nowrap',
            //                                     width: isMobile ? 220 : 280,
            //                                 }}
            //                             >
            //                                 {product.productName}
            //                             </Typography>
            //                             <Typography
            //                                 variant="body2"
            //                                 sx={{
            //                                     textAlign: 'right',
            //                                     whiteSpace: 'nowrap',
            //                                     width: 20,
            //                                 }}
            //                             >
            //                                 {totalQuantity}
            //                             </Typography>
            //                         </Box>
            //                     )}
            //                 )}
            //             </Box>
            //         );
            //     },
            // },         
            ...categoryColumns,            
            {
                field: "totalAmount",
                headerName: "TotalAmount",
                headerClassName: 'theme-column--header',
                width: 150,
                align: 'right',
                valueFormatter: (params) => {
                    return params ? params.toLocaleString() : '';
                },
            },
            {
                field: "preorderAmount",
                headerName: "PreorderAmount",
                headerClassName: 'theme-column--header',
                width: 150,
                align: 'right',
                valueFormatter: (params) => {
                    return params ? params.toLocaleString() : '';
                },
            },
            {
                field: "directAmount",
                headerName: "DirectAmount",
                headerClassName: 'theme-column--header',
                width: 150,
                align: 'right',
                valueFormatter: (params) => {
                    return params ? params.toLocaleString() : '';
                },
            },        
            {
                field: "totalCommission",
                headerName: "TotalCommission",
                headerClassName: 'theme-column--header',
                width: 150,
                align: 'right',
                valueFormatter: (params) => {
                    return params ? params.toLocaleString() : '';
                },
            },
            {
                field: "preorderCommission",
                headerName: "PreorderCommission",
                headerClassName: 'theme-column--header',
                width: 150,
                align: 'right',
                valueFormatter: (params) => {
                    return params ? params.toLocaleString() : '';
                },
            },
            {
                field: "directCommission",
                headerName: "DirectCommission",
                headerClassName: 'theme-column--header',
                width: 150,
                align: 'right',
                valueFormatter: (params) => {
                    return params ? params.toLocaleString() : '';
                },
            },        
            {
                field: "payableAmount",
                headerName: "PayableAmount",
                headerClassName: 'theme-column--header',
                width: 150,
                align: 'right',
                valueFormatter: (params) => {
                    return params ? params.toLocaleString() : '';
                },
            },    
            {
                field: "crewCount",
                headerName: "CrewCount",
                headerClassName: 'theme-column--header',
                width: 100,
                align: 'right',
                valueFormatter: (params) => {
                    return params ? params.toLocaleString() : '';
                },
            },
            {
                field: "eachCrewTotalCommission",
                headerName: "Commission1Crew",
                headerClassName: 'theme-column--header',
                width: 150,
                align: 'right',
                valueFormatter: (params) => {
                    if (!params) return ''; 
                    const roundedValue = Math.ceil(params); 
                    return roundedValue.toLocaleString(); 
                },
            },
        ]
    }, [timeMode, isMobile, crewRanks, invoices]);
    
    const hiddenColumns = useMemo(() => {
        return {
            _id: false,
            no: false,   
            createdAt: false,
            updatedAt: false,
            // flightNo: false,
            // crewRoute: false,
            totalAmount: false,
            preorderAmount: false,
            directAmount: false,
            totalCommission: false,  
            preorderCommission: false,  
            directCommission: false,  
            // payableAmount: false,  
        }
    }, []);

    const nonMobileHiddenColumns = useMemo(() => {
        return {
            _id: false,      
            // createdAt: false,
            updatedAt: false, 
            totalAmount: false,
            preorderAmount: false,
            directAmount: false,
            totalCommission: false,  
            preorderCommission: false,  
            directCommission: false, 
        }
    }, []);
    
    const actionColumn = useMemo(() => [{
        field: "action",
        headerName: "Action",
        width: 80,
        filterable: false,      
        sortable: false,
        renderCell: (params) => (
          <Box >
            <Button
                aria-label="View"
                style={{ color: theme.palette.primary.dark }}
                onClick={() => {
                    setSelectedInvoiceId(params.row._id);
                    setSelectedCrewCode(params.row.crewCode);
                    setSelectedCrewName(params.row.crewName);
                    setSelectedFlights(params.row.selectedFlights);
                    setCrewMembers(params.row.crewMembers);
                    const latestProducts = params.row.products.map((product) => ({
                        ...product,
                        price: product.price, 
                    }));
                    setSelectedProducts(latestProducts);
                    handleViewClick(params.row._id)
                }}
            >
              <VisibilityIcon />
            </Button>
          </Box>
        ),
    }], [theme, handleViewClick]); 
    
    const [columnVisibilityModel, setColumnVisibilityModel] = useState(
        isMobile ? hiddenColumns : nonMobileHiddenColumns
    );

    useEffect(() => {
        setColumnVisibilityModel(isMobile ? hiddenColumns : nonMobileHiddenColumns);
    }, [isMobile, hiddenColumns, nonMobileHiddenColumns]);
    
    
    return (
        <Box className="main-container-box">            
            <FlexBetween>
                <Header 
                    title="Invoices" 
                    subtitle={`${loading ? 'Loading...' : 'List of Invoices'}`}
                />                 
            </FlexBetween>              
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">{error?.message}</Typography>
                </FlexCenter>
            ) : (
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                    gap={2}
                >
                    <Box
                        height={LAYOUT_HEIGHT}  
                        sx={{ gridColumn: "span 4" }}    
                    >                            
                        <DataGrid                    
                            getRowId={(row) => row._id}
                            rows={(invoices) || []}
                            columns={[...columns, ...actionColumn]}
                            rowsPerPageOptions={[50, 100, 200]}
                            columnVisibilityModel={columnVisibilityModel}
                            onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}                
                            showToolbar={isMobile ? false : true}   
                            slots={{ toolbar: () => <CustomDataGridToolbar isExpanded={isMobile ? false : true} /> }}  
                            rowSelectionModel={rowSelectionModel}       
                            onRowSelectionModelChange={(newRowSelectionModel) => {
                                setRowSelectionModel(newRowSelectionModel);
                            }}                         
                        />
                    </Box>    
                                        
                    {openInvoiceDialog && 
                        <InvoiceDialog
                            open={openInvoiceDialog}
                            onClose={handleCloseInvoiceDialog}
                            selectedInvoiceId={selectedInvoiceId} 
                            crewCode={selectedCrewCode} 
                            crewName={selectedCrewName}
                            selectedFlights={selectedFlights}
                            crewMembers={crewMembers}
                            selectedProducts={selectedProducts}
                            onInvoiceSaved={handleInvoiceSaved}
                        />
                    }                           
                </Box>
            )}
        </Box>
    );
};

export default Invoices;
